import React from 'react';

import { StyledClientsApiPage } from './ClientsApiPage.style';
import appLogo from '../../ui/assets/images/app-icon.svg';
import { useAppDispatch, useAppSelector } from '../../store/store';
import addressSliceThunks from '../../store/addressSlice/addressSlice.thunks';
import { addressSliceActions } from '../../store/addressSlice/addressSlice.reducer';

const ClientApiPage: React.FC = () => {
  const [apiKey, setApiKey] = React.useState<string>('');
  const [postcode, setPostcode] = React.useState<string>('');
  const [text, setText] = React.useState<string>('');
  const [textOrPostcode, setTextOrPostcode] = React.useState<string>('');
  const [isSuggestionsOpen, setIsSuggestionsOpen] = React.useState<boolean>(false);

  const dispatch = useAppDispatch();
  const adressData = useAppSelector(({ addressState }) => addressState.addresses);
  const suggestions = useAppSelector(({ addressState }) => addressState.suggestions);
  const jsonData = JSON.stringify(adressData, null, 2);
  const trimmedTextOrPostcode = textOrPostcode.trim();

  React.useEffect(() => {
    if (trimmedTextOrPostcode.length < 3) {
      dispatch(addressSliceActions.clearSuggestions());
      setIsSuggestionsOpen(false);
      return undefined;
    }

    let abortRequest: (() => void) | undefined;
    const timeoutId = window.setTimeout(() => {
      const request = dispatch(addressSliceThunks.getAddressSuggestions({
        text: trimmedTextOrPostcode,
        apiKey,
      }));

      abortRequest = request.abort;
      setIsSuggestionsOpen(true);
    }, 400);

    return () => {
      window.clearTimeout(timeoutId);
      abortRequest?.();
    };
  }, [apiKey, dispatch, trimmedTextOrPostcode]);

  const searchByPostalCode = () => {
    setText('');
    setTextOrPostcode('');
    setIsSuggestionsOpen(false);
    dispatch(addressSliceActions.clearSuggestions());
    dispatch(addressSliceThunks.getAddressesByPostcode({ postcode, apiKey }));
  };

  const searchByText = () => {
    setPostcode('');
    setTextOrPostcode('');
    setIsSuggestionsOpen(false);
    dispatch(addressSliceActions.clearSuggestions());
    dispatch(addressSliceThunks.getAddressesByText({ text, apiKey }));
  };

  const searchByTextOrPostcode = () => {
    setPostcode('');
    setText('');
    setIsSuggestionsOpen(false);
    dispatch(addressSliceActions.clearSuggestions());
    dispatch(addressSliceThunks.getAddressesByTextOrPostcode({ text: textOrPostcode, apiKey }));
  };

  const handleTextOrPostcodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTextOrPostcode(event.target.value);
    setIsSuggestionsOpen(true);
  };

  const handleSuggestionSelect = (streetAddress: string) => {
    setTextOrPostcode(streetAddress);
    setIsSuggestionsOpen(false);
    dispatch(addressSliceActions.clearSuggestions());
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(jsonData);
  };

  return (
    <StyledClientsApiPage>
      <img src={appLogo} alt="Logo" className="clients-page__logo" />
      <div className="clients-page__title">Data searching page</div>
      <label className="clients-page__label">Api key</label>
      <input
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
        className="clients-page__input"
        placeholder="Paste here your api key ..."
      />
      <label className="clients-page__label">Postal code search</label>
      <div className="clients-page__input-wrapper">
        <input
          value={postcode}
          onChange={(e) => setPostcode(e.target.value)}
          className="clients-page__input"
          placeholder="Enter postal code here ..."
        />
        <button onClick={searchByPostalCode} className="clients-page__button">Search</button>
      </div>
      <label className="clients-page__label">Address search</label>
      <div className="clients-page__input-wrapper">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="clients-page__input"
          placeholder="Enter search text here ..."
        />
        <button onClick={searchByText} className="clients-page__button">Search</button>
      </div>
      <label className="clients-page__label">Search by postcode or text</label>
      <div className="clients-page__input-wrapper">
        <div className="clients-page__autocomplete">
          <input
            value={textOrPostcode}
            onChange={handleTextOrPostcodeChange}
            onFocus={() => setIsSuggestionsOpen(true)}
            onBlur={() => setIsSuggestionsOpen(false)}
            className="clients-page__input"
            placeholder="Enter search text here ..."
          />
          {isSuggestionsOpen && suggestions.length > 0 && (
            <div className="clients-page__suggestions">
              {suggestions.map((suggestion, index) => (
                <button
                  key={`${suggestion.street_address}-${index}`}
                  type="button"
                  className="clients-page__suggestion"
                  onMouseDown={() => handleSuggestionSelect(suggestion.street_address)}
                >
                  {suggestion.street_address}, postcode: {suggestion.postcode}
                </button>
              ))}
            </div>
          )}
        </div>
        <button onClick={searchByTextOrPostcode} className="clients-page__button">Search</button>
      </div>
      <div className="clients-page__data-wrapper">
        <pre className="clients-page__data">
          {jsonData}
        </pre>
        <button
          onClick={handleCopy}
          className="clients-page__button"
        >
          Copy to clipboard
        </button>
      </div>
    </StyledClientsApiPage>
  );
};

export default ClientApiPage;
